import { forwardRef } from 'react';
import type { NavigateOptions } from 'react-router';

import { InstanceLink } from '@/components/routing';
import type { InstanceLinkProps } from '@/components/routing/InstanceLink/InstanceLink';

import { getButtonClassName, renderButtonContent, type ButtonProps } from '../Button';

type LinkButtonPrimitiveProps = Pick<
  ButtonProps,
  | 'variant'
  | 'size'
  | 'shape'
  | 'isLoading'
  | 'disabled'
  | 'block'
  | 'danger'
  | 'inverse'
  | 'className'
>;

export type LinkButtonProps = LinkButtonPrimitiveProps &
  InstanceLinkProps & {
    toOptions?: NavigateOptions;
  };

const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    {
      to,
      toOptions = {},
      target,
      rel,
      className,
      children,
      variant = 'secondary',
      size = 'medium',
      shape = 'round',
      isLoading = false,
      disabled = false,
      block = false,
      danger,
      inverse,
      ...rest
    },
    ref,
  ) => {
    const { className: computedClassName, loadingSize } = getButtonClassName({
      variant,
      size,
      shape,
      danger,
      inverse,
      block,
      className,
    });
    const isButtonDisabled = disabled || isLoading;

    return (
      <InstanceLink
        data-slot="button"
        ref={ref}
        to={to}
        target={target}
        rel={!rel && target === '_blank' ? 'noreferrer' : rel}
        disabled={isButtonDisabled}
        className={computedClassName}
        {...rest}
        {...toOptions}
      >
        {renderButtonContent({ children, isLoading, loadingSize })}
      </InstanceLink>
    );
  },
);

LinkButton.displayName = 'LinkButton';

export default LinkButton;
